import { useEffect, useMemo, useState } from 'react';
import { useConfirm } from '../widget-ui/confirm-dialog';
import { formatSlotDayShort } from '../widget-lib/format-date';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Button } from '@domcrm/embed-ui';

import { ApiError } from '../widget-api/http-client';
import {
  createBookingForClient,
  createClientMinor,
  deleteClientMinor,
  isTrainerClientSearchReady,
  listAdminClients,
  listClientMinors,
  type AdminClientListItem,
  type RepresentedMinorListItem
} from '../widget-api/trainer-clients';
import {
  fetchTrainerManageSlots,
  fetchTrainerManageSlotsAllBranches,
  type ScheduleSlotListItem
} from '../widget-api/schedule-slots';
import { addBookingAddon, fetchAllowedAddons, type AllowedAddonItem } from '../widget-api/bookings';
import { useEmbedBootstrap } from '../use-widget-bootstrap';
import { useAsync } from '../widget-hooks/useAsync';
import { useTrainerBranchOptions } from '../widget-hooks/useTrainerBranchOptions';
import { useQuerySuffix } from '../widget-hooks/useQuerySuffix';
import { useWidgetSeo } from '../widget-hooks/use-widget-seo';
import { PATH } from '../widget-lib/path-routes';
import { formatPhone } from '../widget-lib/phone-tel';
import { isTrainerOwnOrUnassignedSlot } from '../widget-lib/trainer-slot-utils';
import { EmbedMessageBlock } from '../widget-ui/embed-message-block';

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const inputClass =
  'h-8 w-full rounded-sm border-0 bg-[var(--bg-soft)] px-3 py-1 text-[13px] text-[var(--text)] focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]';

const mobileSearchClass =
  'h-11 w-full rounded-full border-0 bg-[var(--bg-soft)] pl-11 pr-4 text-sm text-[var(--text)] shadow-inner focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]';

function clientTitle(c: AdminClientListItem): string {
  return c.full_name?.trim() || c.email || `Клиент #${c.id}`;
}

function clientInitials(c: AdminClientListItem): string {
  const raw = (c.full_name || c.email || '?').trim();
  return (raw[0] || '?').toUpperCase();
}

function slotDateTimeValue(slot: ScheduleSlotListItem): string {
  return `${slot.slot_date}T${slot.start_time || '00:00:00'}`;
}

export default function TrainerBookClientPage() {
  const confirmAction = useConfirm();
  useWidgetSeo(
    'Запись клиента | Кабинет тренера',
    'Быстрая запись клиента на индивидуальное или групповое занятие.'
  );
  const navigate = useNavigate();
  const suffix = useQuerySuffix();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken, me, meLoading, meError, reloadMe, widgetContour, hasPermission } = useEmbedBootstrap();
  const trainerMobile = widgetContour === 'trainer-mobile';

  const slotIdParam = searchParams.get('slot_id');
  const parsedSlotId = slotIdParam != null && slotIdParam !== '' ? Number.parseInt(slotIdParam, 10) : NaN;
  const validSlotId = Number.isFinite(parsedSlotId) && parsedSlotId > 0 ? parsedSlotId : null;

  const [pickedSlot, setPickedSlot] = useState<ScheduleSlotListItem | null>(null);
  const [clientFilter, setClientFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    const filterVal = clientFilter.trim();
    if (filterVal.length < 2) {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => {
      setDebouncedFilter(filterVal);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientFilter]);

  const [selectedClient, setSelectedClient] = useState<AdminClientListItem | null>(null);
  const [minorId, setMinorId] = useState('');
  const [comment, setComment] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [minorsReloadKey, setMinorsReloadKey] = useState(0);
  const [newMinorName, setNewMinorName] = useState('');
  const [newMinorDob, setNewMinorDob] = useState('');
  const [newMinorConsent, setNewMinorConsent] = useState(false);
  const [minorMutatingId, setMinorMutatingId] = useState<number | null>(null);
  const [allowedAddons, setAllowedAddons] = useState<AllowedAddonItem[]>([]);
  const [selectedAddons, setSelectedAddons] = useState<number[]>([]);
  const [addonsLoading, setAddonsLoading] = useState(false);
  const canEditClientMinors = hasPermission('clients:org_write');

  const range = useMemo(() => {
    const from = new Date();
    const to = addDays(from, 14);
    return { fromDate: toIsoDate(from), toDate: toIsoDate(to) };
  }, []);

  const { branchOptions } = useTrainerBranchOptions(me);
  const branchId = me?.active_branch_id ?? branchOptions[0]?.id ?? null;

  useEffect(() => {
    const svcId = pickedSlot?.service_id;
    if (!svcId || me?.organization_id == null) {
      setAllowedAddons([]);
      setSelectedAddons([]);
      return;
    }
    let cancelled = false;
    setAddonsLoading(true);
    void (async () => {
      try {
        const res = await fetchAllowedAddons(
          svcId,
          me.organization_id as number,
          pickedSlot?.branch_id ?? branchId ?? null,
          null
        );
        if (!cancelled) {
          setAllowedAddons(res.items);
          setSelectedAddons(res.items.filter((a) => a.is_required).map((a) => a.id));
        }
      } catch {
        if (!cancelled) {
          setAllowedAddons([]);
          setSelectedAddons([]);
        }
      } finally {
        if (!cancelled) setAddonsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pickedSlot?.service_id, pickedSlot?.branch_id, me?.organization_id, branchId]);

  function toggleAddon(id: number): void {
    if (allowedAddons.some((a) => a.id === id && a.is_required)) return;
    setSelectedAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const { data: slots, loading: slotsLoading, error: slotsError } = useAsync(
    () => {
      if (!accessToken) {
        return Promise.resolve([]);
      }
      if (branchOptions.length > 0) {
        // Все доступные филиалы сразу (trainer_placements тренера или все филиалы студии у админа).
        return fetchTrainerManageSlotsAllBranches({
          organizationId: me?.organization_id ?? null,
          branchList: branchOptions,
          fromDate: range.fromDate,
          toDate: range.toDate,
          accessToken
        });
      }
      if (branchId == null) {
        return Promise.resolve([]);
      }
      return fetchTrainerManageSlots({
        organizationId: me?.organization_id ?? null,
        branchId,
        fromDate: range.fromDate,
        toDate: range.toDate,
        accessToken
      });
    },
    [accessToken, branchId, branchOptions, range.fromDate, range.toDate, me?.organization_id]
  );

  const visibleSlots = useMemo(() => {
    const allowed = new Set(branchOptions.map((b) => b.id));
    return (slots ?? [])
      .filter((slot) => {
        if (slot.places_left <= 0 || slot.is_available === false) return false;
        if (allowed.size > 0 && slot.branch_id != null && !allowed.has(slot.branch_id)) return false;
        return isTrainerOwnOrUnassignedSlot(slot, me);
      })
      .sort((a, b) => slotDateTimeValue(a).localeCompare(slotDateTimeValue(b)));
  }, [slots, me, branchOptions]);

  useEffect(() => {
    if (!visibleSlots.length || validSlotId == null) return;
    const found = visibleSlots.find((slot) => slot.id === validSlotId) ?? null;
    setPickedSlot(found);
  }, [visibleSlots, validSlotId]);

  const searchReady = isTrainerClientSearchReady(debouncedFilter);

  const { data: clientsResult, loading: clientsLoading, error: clientsError } = useAsync(
    () => {
      if (!accessToken || !searchReady) {
        return Promise.resolve({ items: [], total: 0 });
      }
      // Тренер ищет клиента по всей базе студии: сервер требует q >= 2 и отдаёт не больше 20.
      return listAdminClients(accessToken, { q: debouncedFilter.trim(), limit: 20 });
    },
    [accessToken, searchReady, debouncedFilter]
  );

  const filteredClients = clientsResult?.items ?? [];

  const isChildSlot = pickedSlot?.service_group_type?.toLowerCase() === 'child';

  const { data: minors, loading: minorsLoading, error: minorsError } = useAsync(
    () => {
      if (!accessToken || !selectedClient) {
        return Promise.resolve([] as RepresentedMinorListItem[]);
      }
      if (!isChildSlot && !canEditClientMinors) {
        return Promise.resolve([] as RepresentedMinorListItem[]);
      }
      return listClientMinors(accessToken, selectedClient.id);
    },
    [accessToken, selectedClient?.id, isChildSlot, canEditClientMinors, minorsReloadKey]
  );

  useEffect(() => {
    setMinorId('');
  }, [selectedClient?.id, isChildSlot]);

  async function onAddClientMinor() {
    if (!accessToken || !selectedClient) return;
    const name = newMinorName.trim();
    const dob = newMinorDob.trim();
    if (!name || !dob) {
      setSubmitError('Укажите имя (одно или два слова) и дату рождения подопечного.');
      return;
    }
    if (name.split(/\s+/).length > 2) {
      setSubmitError('Имя подопечного — одно или два слова, без фамилии и отчества.');
      return;
    }
    if (!newMinorConsent) {
      setSubmitError('Подтвердите, что законный представитель подписал согласие на обработку данных ребёнка.');
      return;
    }
    setSubmitError(null);
    setMinorMutatingId(-1);
    try {
      await createClientMinor(accessToken, selectedClient.id, {
        name,
        date_of_birth: dob,
        consent_confirmed: true,
      });
      setNewMinorName('');
      setNewMinorDob('');
      setNewMinorConsent(false);
      setMinorsReloadKey((k) => k + 1);
    } catch (error) {
      if (error instanceof ApiError) {
        setSubmitError(error.detail);
      } else {
        setSubmitError(error instanceof Error ? error.message : 'Не удалось добавить подопечного');
      }
    } finally {
      setMinorMutatingId(null);
    }
  }

  async function onDeleteClientMinor(minor: RepresentedMinorListItem) {
    if (!accessToken || !selectedClient) return;
    const ok = await confirmAction({
      title: 'Удалить подопечного из карточки?',
      message: minor.name,
      confirmLabel: 'Удалить',
      danger: true
    });
    if (!ok) return;
    setSubmitError(null);
    setMinorMutatingId(minor.id);
    try {
      await deleteClientMinor(accessToken, selectedClient.id, minor.id);
      if (minorId === String(minor.id)) setMinorId('');
      setMinorsReloadKey((k) => k + 1);
    } catch (error) {
      if (error instanceof ApiError) {
        setSubmitError(error.detail);
      } else {
        setSubmitError(error instanceof Error ? error.message : 'Не удалось удалить');
      }
    } finally {
      setMinorMutatingId(null);
    }
  }

  function pickSlot(slot: ScheduleSlotListItem) {
    setPickedSlot(slot);
    setComment('');
    setSubmitError(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('slot_id', String(slot.id));
      return next;
    });
  }

  function clearSlotPick() {
    setPickedSlot(null);
    setComment('');
    setSubmitError(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('slot_id');
      return next;
    });
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitError(null);
    if (!accessToken || !pickedSlot || !selectedClient) {
      setSubmitError('Выберите слот и клиента.');
      return;
    }
    if (pickedSlot.places_left <= 0) {
      setSubmitError('В этом слоте нет свободных мест.');
      return;
    }
    if (isChildSlot) {
      if (minorsLoading) {
        setSubmitError('Подождите загрузки списка детей.');
        return;
      }
      if (minorsError) {
        setSubmitError(minorsError);
        return;
      }
      if ((minors?.length ?? 0) > 0 && !minorId) {
        setSubmitError('Выберите ребёнка для детского занятия.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const created = await createBookingForClient(accessToken, {
        slot_id: pickedSlot.id,
        client_id: selectedClient.id,
        comment: comment.trim() || null,
        represented_minor_id: isChildSlot && minorId ? Number.parseInt(minorId, 10) : null
      });
      if (selectedAddons.length > 0 && created?.id) {
        const required = new Set(allowedAddons.filter((a) => a.is_required).map((a) => a.id));
        for (const addonId of selectedAddons) {
          // Обязательные допы staff-бронь добавляет сама (CreateBooking); повторный POST дал бы 409.
          if (required.has(addonId)) continue;
          try {
            await addBookingAddon(
              created.id,
              { addon_service_id: addonId, payment_method: 'cash' },
              accessToken
            );
          } catch (addonErr) {
            const msg = addonErr instanceof ApiError ? addonErr.detail : 'Не удалось добавить доп';
            setSubmitError(`Запись создана, но доп не добавлен: ${msg}`);
            return;
          }
        }
      }
      navigate(`${trainerMobile ? PATH.trainerMySessions : PATH.trainerWorkSchedule}${suffix}`);
    } catch (error) {
      if (error instanceof ApiError) {
        setSubmitError(error.detail);
      } else {
        setSubmitError(error instanceof Error ? error.message : 'Не удалось создать запись');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const slotPickerList =
    slotsLoading ? (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
        ))}
      </div>
    ) : slotsError ? (
      <EmbedMessageBlock variant="error" size="field" role="alert">
        {slotsError}
      </EmbedMessageBlock>
    ) : !visibleSlots.length ? (
      <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/80 px-4 py-6 text-center">
        <p className="text-sm font-medium text-[var(--text)]">Нет свободных слотов для записи</p>
        <p className="mt-1 text-xs leading-snug text-[var(--text-muted)]">
          В ближайшие две недели нет занятий, где вы ведущий и есть места. Проверьте расписание в CRM.
        </p>
      </div>
    ) : (
      <ul className="space-y-2">
        {visibleSlots.map((slot) => {
          const time = `${(slot.start_time || '').slice(0, 5)} — ${(slot.end_time || '').slice(0, 5)}`;
          return (
            <li key={slot.id}>
              <button
                type="button"
                className="w-full rounded-2xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-2.5 text-left text-sm transition-colors hover:bg-[var(--bg-elevated)]"
                onClick={() => pickSlot(slot)}
              >
                <span className="font-semibold text-[var(--text)]">{slot.service_name}</span>
                <span className="mt-0.5 block text-xs text-[var(--text-muted)]">
                  {formatSlotDayShort(slot.slot_date)} {time} · свободно {slot.places_left}
                  {branchOptions.find((b) => b.id === slot.branch_id)?.name
                    ? ` · ${branchOptions.find((b) => b.id === slot.branch_id)?.name}`
                    : ''}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );

  const clientPickerSection = (
    <section className="space-y-3">
      {!trainerMobile ? <h2 className="text-sm font-medium text-[var(--text)]">Шаг 2: клиент</h2> : null}
      {!trainerMobile ? (
        <input
          type="search"
          className={inputClass}
          placeholder="Имя или телефон (минимум 2 символа)"
          value={clientFilter}
          onChange={(ev) => setClientFilter(ev.target.value)}
          autoComplete="off"
        />
      ) : null}
      {!searchReady ? (
        <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/80 px-3 py-6 text-center">
          <p className="text-sm font-medium text-[var(--text)]">Введите имя или телефон</p>
          <p className="mt-1 text-xs text-[var(--text-muted)]">Минимум 2 символа — и мы найдём клиента по всей базе студии.</p>
        </div>
      ) : clientsLoading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
          ))}
        </div>
      ) : clientsError ? (
        <EmbedMessageBlock variant="error" size="field" role="alert">
          {clientsError}
        </EmbedMessageBlock>
      ) : trainerMobile ? (
        <ul className="space-y-2">
          {filteredClients.length === 0 ? (
            <li className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/80 px-3 py-6 text-center">
              <p className="text-sm font-medium text-[var(--text)]">Клиенты не найдены</p>
              <p className="mt-1 text-xs text-[var(--text-muted)]">Измените запрос или создайте нового клиента.</p>
            </li>
          ) : (
            filteredClients.map((client) => {
              const active = selectedClient?.id === client.id;
              return (
                <li
                  key={client.id}
                  className={`rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-3 shadow-sm ${
                    active ? 'ring-2 ring-[var(--accent)]' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--bg-soft)] text-sm font-bold text-[var(--accent)]">
                      {clientInitials(client)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[var(--text)]">{clientTitle(client)}</p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {client.phone ? formatPhone(client.phone) : '—'}
                        {client.email ? <span className="ml-1">· {client.email}</span> : null}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded-full bg-[var(--accent-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--accent-fg)]"
                          onClick={() => {
                            setSelectedClient(client);
                            setSubmitError(null);
                          }}
                        >
                          Выбрать
                        </button>
                        <span className="text-xs text-[var(--text-muted)]">Профиль в CRM</span>
                      </div>
                    </div>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      ) : (
        <ul className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-1">
          {filteredClients.length === 0 ? (
            <li className="px-2 py-4 text-center">
              <p className="text-sm font-medium text-[var(--text)]">Нет совпадений</p>
              <p className="mt-1 text-xs text-[var(--text-muted)]">Попробуйте другой телефон или имя.</p>
            </li>
          ) : (
            filteredClients.map((client) => {
              const active = selectedClient?.id === client.id;
              return (
                <li key={client.id}>
                  <button
                    type="button"
                    className={`w-full rounded-sm px-2 py-2 text-left text-sm ${
                      active ? 'bg-[var(--bg-elevated)] text-[var(--accent)]' : 'hover:bg-[var(--bg-elevated)]'
                    }`}
                    onClick={() => {
                      setSelectedClient(client);
                      setSubmitError(null);
                    }}
                  >
                    <span className="font-medium">{clientTitle(client)}</span>
                    {client.phone ? <span className="block text-xs text-[var(--text-muted)]">{formatPhone(client.phone)}</span> : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </section>
  );

  const bookingForm =
    selectedClient && pickedSlot ? (
      <form className="space-y-4" onSubmit={onSubmit}>
        <p className="text-sm text-[var(--text-muted)]">
          Выбран: <strong className="text-[var(--text)]">{selectedClient.full_name?.trim() || selectedClient.email}</strong>
        </p>

        {canEditClientMinors ? (
          <div className="space-y-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] px-3 py-3">
            <p className="text-sm font-medium text-[var(--text)]">Подопечные в карточке</p>
            {minorsLoading ? (
              <div className="h-8 animate-pulse rounded bg-[var(--bg-elevated)]" aria-busy="true" />
            ) : minorsError ? (
              <EmbedMessageBlock variant="error" size="field" role="alert">
                {minorsError}
              </EmbedMessageBlock>
            ) : (
              <ul className="space-y-1 text-sm">
                {(minors ?? []).length === 0 ? (
                  <li className="text-[var(--text-muted)]">Пока не добавлены — нужны для детских занятий.</li>
                ) : (
                  (minors ?? []).map((m) => (
                    <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-[var(--bg-elevated)] px-2 py-1.5">
                      <span className="text-[var(--text)]">
                        {m.name}
                        <span className="ml-2 text-xs text-[var(--text-muted)]">{m.date_of_birth}</span>
                      </span>
                      <button
                        type="button"
                        className="text-xs font-semibold text-[var(--error-text)] disabled:opacity-50"
                        disabled={minorMutatingId === m.id}
                        onClick={() => void onDeleteClientMinor(m)}
                      >
                        {minorMutatingId === m.id ? '…' : 'Удалить'}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-end">
              <div className="min-w-0 flex-1">
                <label className="mb-0.5 block text-xs text-[var(--text-muted)]" htmlFor="trainer-new-minor-name">
                  Имя подопечного (1–2 слова)
                </label>
                <input
                  id="trainer-new-minor-name"
                  className={inputClass}
                  value={newMinorName}
                  onChange={(ev) => setNewMinorName(ev.target.value)}
                  autoComplete="name"
                />
              </div>
              <div>
                <label className="mb-0.5 block text-xs text-[var(--text-muted)]" htmlFor="trainer-new-minor-dob">
                  Дата рождения
                </label>
                <input
                  id="trainer-new-minor-dob"
                  type="date"
                  className={inputClass}
                  value={newMinorDob}
                  onChange={(ev) => setNewMinorDob(ev.target.value)}
                />
              </div>
              <button
                type="button"
                className="h-8 rounded-sm bg-[var(--accent-bg)] px-3 text-[13px] font-semibold text-[var(--accent-fg)] disabled:opacity-50"
                disabled={minorMutatingId !== null}
                onClick={() => void onAddClientMinor()}
              >
                {minorMutatingId === -1 ? 'Добавляем…' : 'Добавить'}
              </button>
            </div>
            <label className="flex items-start gap-2 text-xs text-[var(--text-muted)]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={newMinorConsent}
                onChange={(ev) => setNewMinorConsent(ev.target.checked)}
              />
              <span>Законный представитель подписал согласие на обработку данных ребёнка (имя, дата рождения).</span>
            </label>
          </div>
        ) : null}

        {isChildSlot ? (
          <div>
            <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="trainer-book-minor">
              Ребёнок (участник)
            </label>
            {minorsLoading ? (
              <div className="h-9 animate-pulse rounded-sm bg-[var(--bg-soft)]" aria-busy="true" />
            ) : minorsError ? (
              <EmbedMessageBlock variant="error" size="field" role="alert">
                {minorsError}
              </EmbedMessageBlock>
            ) : (
              <select
                id="trainer-book-minor"
                className={inputClass}
                value={minorId}
                onChange={(ev) => setMinorId(ev.target.value)}
                required={Boolean(minors?.length)}
              >
                <option value="">
                  {(minors?.length ?? 0) === 0 ? 'Нет детей в карточке — добавьте в CRM' : '— выберите ребёнка —'}
                </option>
                {(minors ?? []).map((minor) => (
                  <option key={minor.id} value={String(minor.id)}>
                    {minor.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        ) : null}

        <div>
          <label className="mb-1 block text-[13px] text-[var(--text-muted)]" htmlFor="trainer-book-comment">
            Комментарий
          </label>
          <input
            id="trainer-book-comment"
            className={inputClass}
            value={comment}
            onChange={(ev) => setComment(ev.target.value)}
            placeholder="Например, придёт с другом"
            autoComplete="off"
          />
        </div>

        {addonsLoading ? (
          <p className="text-xs text-[var(--text-muted)]">Загружаем допы…</p>
        ) : allowedAddons.length > 0 ? (
          <div>
            <p className="mb-1 block text-[13px] text-[var(--text-muted)]">Доп. услуги (по желанию)</p>
            <div className="space-y-1.5 rounded-xl bg-[var(--bg-soft)] p-3">
              {allowedAddons.map((a) => (
                <label key={a.id} className="flex cursor-pointer items-center gap-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedAddons.includes(a.id)}
                    disabled={Boolean(a.is_required)}
                    onChange={() => toggleAddon(a.id)}
                    className="h-4 w-4 shrink-0"
                  />
                  <span className="min-w-0 flex-1 text-[var(--text)]">
                    {a.name}
                    {a.is_required ? (
                      <span className="ml-1.5 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                        обязательно
                      </span>
                    ) : null}
                  </span>
                  <span className="shrink-0 text-[13px] font-semibold tabular-nums text-[var(--text)]">
                    {Number(a.price) > 0 ? `+${Math.round(Number(a.price)).toLocaleString('ru-RU')} ₽` : 'Бесплатно'}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ) : null}

        {submitError ? <EmbedMessageBlock variant="error" size="field">{submitError}</EmbedMessageBlock> : null}

        <Button type="submit" disabled={submitting || pickedSlot.places_left <= 0}>
          {submitting ? 'Запись…' : 'Записать клиента'}
        </Button>
      </form>
    ) : null;

  if (meLoading) {
    return (
      <div className="w-full min-w-0 space-y-3" aria-busy="true">
        <div className="h-7 w-48 animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="h-4 w-full max-w-md animate-pulse rounded bg-[var(--bg-soft)]" />
        <div className="h-16 animate-pulse rounded-2xl bg-[var(--bg-soft)]" />
      </div>
    );
  }

  if (meError) {
    return (
      <div className="w-full min-w-0 space-y-3">
        <EmbedMessageBlock variant="error" size="field" role="alert">
          {meError}
        </EmbedMessageBlock>
        <button
          type="button"
          className="text-sm font-medium text-[var(--accent)] underline-offset-2 hover:underline"
          onClick={() => reloadMe()}
        >
          Загрузить профиль снова
        </button>
      </div>
    );
  }

  if (branchId == null && branchOptions.length === 0) {
    return (
      <div className="space-y-4 w-full min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">Записать клиента</h1>
        <p className="text-sm text-[var(--text-muted)]">
          У вас нет доступных филиалов. Обратитесь к администратору студии.
        </p>
      </div>
    );
  }

  if (trainerMobile) {
    return (
      <div className="space-y-4">
        <div className="sticky top-0 z-20 -mx-1 bg-[color-mix(in_srgb,var(--bg)_92%,transparent)] pb-2 pt-1 backdrop-blur-sm">
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" aria-hidden>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
              </svg>
            </span>
            <input
              type="search"
              className={mobileSearchClass}
              placeholder="Имя или телефон (минимум 2 символа)"
              value={clientFilter}
              onChange={(ev) => setClientFilter(ev.target.value)}
              autoComplete="off"
            />
          </div>
          <p className="mt-2 px-1 text-xs text-[var(--text-muted)]">
            Начните вводить имя или телефон клиента — покажем совпадения по всей базе студии.
          </p>
        </div>
        {selectedClient && pickedSlot ? bookingForm : null}

        {!pickedSlot ? (
          <>
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-[var(--text)]">Занятие</h2>
              {slotPickerList}
            </section>

            {!searchReady ? (
              <section className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/80 px-3 py-6 text-center">
                <p className="text-sm font-medium text-[var(--text)]">Введите имя или телефон</p>
                <p className="mt-1 text-xs text-[var(--text-muted)]">Минимум 2 символа — и мы найдём клиента в базе студии.</p>
              </section>
            ) : (
              <section className="space-y-2">
                <h2 className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Результаты поиска</h2>
                {filteredClients.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[var(--border)] bg-[var(--bg-soft)]/80 px-3 py-6 text-center">
                    <p className="text-sm font-medium text-[var(--text)]">Клиенты не найдены</p>
                    <p className="mt-1 text-xs text-[var(--text-muted)]">Попробуйте другой телефон или имя.</p>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {filteredClients.map((client) => (
                      <li
                        key={client.id}
                        className="flex gap-3 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-3 shadow-sm"
                      >
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--bg-soft)] text-sm font-bold text-[var(--accent)]">
                          {clientInitials(client)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-[var(--text)]">{clientTitle(client)}</p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {client.phone ? formatPhone(client.phone) : '—'}
                            {client.email ? <span className="ml-1">· {client.email}</span> : null}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded-full bg-[var(--accent-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--accent-fg)]"
                              onClick={() => {
                                setSelectedClient(client);
                                setSubmitError(null);
                              }}
                            >
                              Выбрать для записи
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {selectedClient && !pickedSlot ? (
              <EmbedMessageBlock variant="info" size="field" className="w-full">
                Клиент выбран. Нажмите на слот в списке выше, чтобы перейти к подтверждению записи.
              </EmbedMessageBlock>
            ) : null}

          </>
        ) : (
          <>
            <section className="space-y-2 rounded-2xl border border-[var(--border)] bg-[var(--bg-elevated)] p-3 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-[var(--text)]">{pickedSlot.service_name}</p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {formatSlotDayShort(pickedSlot.slot_date)} {(pickedSlot.start_time || '').slice(0, 5)} — {(pickedSlot.end_time || '').slice(0, 5)}
                  </p>
                  {pickedSlot.places_left <= 0 ? (
                    <EmbedMessageBlock className="mt-1" variant="error" size="field">
                      Нет свободных мест.
                    </EmbedMessageBlock>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="text-xs font-semibold text-[var(--accent)] underline-offset-2 hover:underline"
                  onClick={clearSlotPick}
                >
                  Другой слот
                </button>
              </div>
            </section>
            {clientPickerSection}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full min-w-0">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">Записать клиента</h1>
        <p className="text-sm text-[var(--text-muted)]">
          Выберите занятие и клиента организации. Для детских услуг нужно указать ребёнка.
        </p>
      </div>

      {!pickedSlot ? (
        <section className="space-y-3">
          <h2 className="text-sm font-medium text-[var(--text)]">Шаг 1: занятие</h2>
          {slotPickerList}
        </section>
      ) : (
        <>
          <section className="space-y-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-3 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium text-[var(--text)]">{pickedSlot.service_name}</p>
                <p className="text-[var(--text-muted)]">
                  {formatSlotDayShort(pickedSlot.slot_date)} {(pickedSlot.start_time || '').slice(0, 5)} — {(pickedSlot.end_time || '').slice(0, 5)}
                </p>
                {pickedSlot.places_left <= 0 ? (
                  <EmbedMessageBlock className="mt-1" variant="error" size="field">
                    Нет свободных мест.
                  </EmbedMessageBlock>
                ) : null}
              </div>
              <button
                type="button"
                className="text-[13px] text-[var(--accent)] underline-offset-2 hover:underline"
                onClick={clearSlotPick}
              >
                Другой слот
              </button>
            </div>
          </section>

          {clientPickerSection}
          {bookingForm}
        </>
      )}

    </div>
  );
}
